<template>
    <Dialog v-bind="primeVueProps" ref="dialog">
        <slot />
    </Dialog>
</template>

<script>
import { computed, useAttrs, useTemplateRef } from 'vue';
/**
 * EModal - Abstraction wrapper around PrimeVue's Dialog.
 * It supports v-model for visibility and a `title` prop for the header, which is translated.
 *
 * All other attributes are passed down to the underlying component.
 */
export default {
    name: 'EModal',
    inheritAttrs: false,
    props: {
        /** v-model visibility; optional so v-model:active can use attrs.active instead */
        modelValue: {
            type: Boolean,
            default: undefined,
        },
        title: {
            type: String,
            default: null,
        },
        fullScreen: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['update:modelValue'],
    setup(props, { emit }) {
        const attrs = useAttrs();
        const dialog = useTemplateRef('dialog');

        const mergePt = (internal, external) => {
            if (!external) {
                return internal;
            }

            const merged = { ...internal };
            Object.keys(external).forEach(key => {
                if (key.startsWith('pc')) {
                    merged[key] = external[key];
                    return;
                }
                merged[key] = {
                    ...(internal[key] ?? {}),
                    ...external[key],
                    class: [internal[key]?.class, external[key]?.class].filter(Boolean).join(' '),
                };
            });
            return merged;
        };

        const primeVueProps = computed(() => {
            const { modelValue: attrsModelValue, active, pt: externalPt, onClose, ...rest } = attrs;
            const visible = props.modelValue !== undefined ? props.modelValue : (attrsModelValue ?? active ?? false);
            const newProps = {
                ...rest,
                visible,
                'onUpdate:visible': value => {
                    emit('update:modelValue', value);
                },
                onHide: () => {
                    onClose?.();
                },
                modal: true,
                dismissableMask: true,
                closable: false,
                maximizable: props.fullScreen,
                onShow: () => {
                    if (props.fullScreen && dialog.value) {
                        dialog.value.maximized = true;
                    }
                },
                pt: mergePt(
                    {
                        header: { class: 'tw:p-0 tw:border-b-0' },
                        footer: { class: 'tw:p-0 tw:border-t-0' },
                        content: { class: 'tw:p-0' },
                        root: { class: 'tw:overflow-hidden' },
                        mask: { class: 'tw:dark:bg-black/40!' },
                    },
                    externalPt
                ),
            };
            if (props.title) {
                newProps.header = props.title;
            }
            return newProps;
        });

        return { primeVueProps, dialog };
    },
};
</script>